import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { getFormattedTimeStamp } from "utils/dateUtils";
import CertTypeChip from "../CertTypeChip";
import { SmimeCert } from "@dashboard-v3/api";

type Params = {
  cert: SmimeCert;
  open: boolean;
  onClose: () => void;
};

export default function Details({ cert, open, onClose }: Params) {
  const { t } = useTranslation("smime");

  if (!cert) return null;

  const emails = cert.emails.length
    ? cert.emails.join(", ")
    : t("certDetails.none");

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">{cert.friendlyName}</Typography>

            <Box
              fontSize=".7rem"
              color="gray"
              style={{ textTransform: "uppercase" }}
            >
              {t("certDetails.subTitle")}
            </Box>
          </Box>
          <CertTypeChip cert={cert} />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column">
          {cert.description && (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                {t("certDetails.description")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {cert.description}
              </Typography>
            </Box>
          )}

          <Box mb={2}>
            <Divider />
          </Box>

          <Box mb={2} display="flex">
            <Box flex={1} mr={2}>
              <Typography variant="subtitle2" gutterBottom>
                {t("certDetails.format")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {cert.format}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle2" gutterBottom>
                {t("certDetails.version")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {cert.version || t("certDetails.notAvailable")}
              </Typography>
            </Box>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.serial")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {cert.serialNumber || t("certDetails.notAvailable")}
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.subject")}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ wordBreak: "break-all" }}
            >
              {cert.subjectDN || t("certDetails.notAvailable")}
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.issuer")}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ wordBreak: "break-all" }}
            >
              {cert.issuerDN || t("certDetails.notAvailable")}
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.emails")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {emails}
            </Typography>
          </Box>

          <Box mb={2}>
            <Divider />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.capabilities")}
            </Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" mt={1}>
              <Flag
                value={cert.isSelfSigned}
                label={t("certDetails.selfSigned")}
              />
              <Flag
                value={cert.hasPrivateKey}
                label={t("certDetails.hasPrivateKey")}
              />
              <Flag value={cert.canSign} label={t("certDetails.canSign")} />
              <Flag
                value={cert.canEncrypt}
                label={t("certDetails.canEncrypt")}
              />
              <Flag
                value={cert.canDecrypt}
                label={t("certDetails.canDecrypt")}
              />
              <Flag value={cert.canVerify} label={t("certDetails.canVerify")} />
            </Box>
          </Box>

          <Box mb={2}>
            <Divider />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.validityPeriod")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("certDetails.fromTo", {
                from: formatDate(cert.validFrom),
                to: formatDate(cert.validUntil),
              })}
            </Typography>
          </Box>

          <Box mb={2}>
            <Divider />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t("certDetails.uploadedOn")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {getFormattedTimeStamp(cert.uploadedAt, true)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("certDetails.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Flag({ value, label }: { value: boolean; label: string }) {
  const { t } = useTranslation("common");

  return (
    <Box mr={1} mb={1}>
      <Chip
        size="small"
        variant="outlined"
        color="default"
        label={
          <Box display="flex" alignItems="center" gridGap=".8rem">
            {label}

            <Box
              fontWeight={500}
              color={value ? "success.main" : "secondary.main"}
            >
              {value ? t("yes") : t("no")}
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
